/**
 * @lumir-company/editor - Link Preview API Handler
 *
 * 서버 사이드 전용 모듈입니다. Next.js App Router, Remix, SvelteKit 등
 * Web API 표준(Request/Response)을 지원하는 프레임워크에서 사용할 수 있습니다.
 *
 * Next.js App Router 사용 예시:
 * ```ts
 * // src/app/api/link-preview/route.ts
 * export { GET } from "@lumir-company/editor/api/link-preview";
 * ```
 */

export interface LinkMetadata {
  url: string;
  title: string;
  description?: string;
  image?: string;
  domain: string;
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function decodeHtmlEntity(str: string): string {
  return str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, "/");
}

function getMetaContent(
  html: string,
  property: string,
  name?: string
): string | null {
  const patterns = [
    new RegExp(
      `<meta\\s+property=["']${property}["']\\s+content=["']([^"']+)["']`,
      "i"
    ),
    new RegExp(
      `<meta\\s+content=["']([^"']+)["']\\s+property=["']${property}["']`,
      "i"
    ),
  ];

  if (name) {
    patterns.push(
      new RegExp(
        `<meta\\s+name=["']${name}["']\\s+content=["']([^"']+)["']`,
        "i"
      ),
      new RegExp(
        `<meta\\s+content=["']([^"']+)["']\\s+name=["']${name}["']`,
        "i"
      )
    );
  }

  for (const pattern of patterns) {
    const match = html.match(pattern);
    if (match?.[1]) return match[1].trim();
  }

  return null;
}

/**
 * HTML 문자열에서 Open Graph / Twitter Card 메타데이터를 파싱합니다.
 * 커스텀 서버 구현 시 직접 사용할 수 있습니다.
 */
export function parseMetaTags(html: string, baseUrl: string): LinkMetadata {
  const domain = extractDomain(baseUrl);
  const metadata: LinkMetadata = { url: baseUrl, title: domain, domain };

  const ogTitle = getMetaContent(html, "og:title");
  const ogDescription = getMetaContent(html, "og:description");
  const ogImage = getMetaContent(html, "og:image");
  const ogUrl = getMetaContent(html, "og:url");

  const twitterTitle = getMetaContent(html, "", "twitter:title");
  const twitterDescription = getMetaContent(html, "", "twitter:description");
  const twitterImage = getMetaContent(html, "", "twitter:image");

  const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
  const descriptionMatch = html.match(
    /<meta\s+name=["']description["']\s+content=["']([^"']+)["']/i
  );

  if (ogTitle) {
    metadata.title = decodeHtmlEntity(ogTitle);
  } else if (twitterTitle) {
    metadata.title = decodeHtmlEntity(twitterTitle);
  } else if (titleMatch?.[1]) {
    metadata.title = decodeHtmlEntity(titleMatch[1].trim());
  }

  if (ogDescription) {
    metadata.description = decodeHtmlEntity(ogDescription);
  } else if (twitterDescription) {
    metadata.description = decodeHtmlEntity(twitterDescription);
  } else if (descriptionMatch?.[1]) {
    metadata.description = decodeHtmlEntity(descriptionMatch[1].trim());
  }

  let imageUrl: string | undefined;
  if (ogImage) {
    imageUrl = ogImage;
  } else if (twitterImage) {
    imageUrl = twitterImage;
  }

  if (imageUrl) {
    imageUrl = decodeHtmlEntity(imageUrl);
    if (imageUrl.trim()) {
      try {
        metadata.image = new URL(imageUrl, baseUrl).toString();
      } catch {
        metadata.image = undefined;
      }
    }
  }

  if (ogUrl) {
    try {
      metadata.url = new URL(ogUrl, baseUrl).toString();
    } catch {
      // keep original URL
    }
  }

  return metadata;
}

/**
 * URL에서 메타데이터를 가져옵니다 (서버 사이드 전용).
 * Express, Fastify 등 커스텀 서버에서 직접 사용할 수 있습니다.
 */
export async function fetchUrlMetadata(url: string): Promise<LinkMetadata> {
  const targetUrl = new URL(url);
  if (!["http:", "https:"].includes(targetUrl.protocol)) {
    throw new Error("Only http and https URLs are allowed");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(targetUrl.toString(), {
      signal: controller.signal,
      headers: {
        "User-Agent":
          "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
        "Accept-Language": "ko-KR,ko;q=0.9,en-US;q=0.8,en;q=0.7",
      },
      redirect: "follow",
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch URL: ${response.status} ${response.statusText}`
      );
    }

    const html = await response.text();
    return parseMetaTags(html, targetUrl.toString());
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

function jsonResponse(data: unknown, status = 200): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * 링크 프리뷰 메타데이터 조회 핸들러 (Web API 표준 Request/Response).
 * Next.js App Router, Remix, SvelteKit 등에서 re-export하여 사용합니다.
 *
 * @example
 * // Next.js: src/app/api/link-preview/route.ts
 * export { linkPreviewHandler as GET } from "@lumir-company/editor/api/link-preview";
 */
export async function linkPreviewHandler(request: Request): Promise<Response> {
  const { searchParams } = new URL(request.url);
  const url = searchParams.get("url");

  if (!url) {
    return jsonResponse({ error: "url parameter is required" }, 400);
  }

  let targetUrl: URL;
  try {
    targetUrl = new URL(url);
    if (!["http:", "https:"].includes(targetUrl.protocol)) {
      return jsonResponse(
        { error: "Only http and https URLs are allowed" },
        400
      );
    }
  } catch {
    return jsonResponse({ error: "Invalid URL format" }, 400);
  }

  try {
    const metadata = await fetchUrlMetadata(targetUrl.toString());
    return jsonResponse(metadata);
  } catch (error: any) {
    if (error.name === "AbortError") {
      return jsonResponse({ error: "Request timeout" }, 408);
    }

    console.error("Error fetching link metadata:", error);
    return jsonResponse({ error: "Failed to fetch link metadata" }, 500);
  }
}
